import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { api, inr } from "@/lib/api";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { TrendingUp, Receipt } from "lucide-react";

export default function ParentReports() {
  const { user } = useAuth();
  const [children, setChildren] = useState([]);
  const [studentId, setStudentId] = useState(null);
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);

  useEffect(() => {
    if (user?.role === "parent") {
      api.get("/parent/children").then(r => {
        setChildren(r.data);
        if (r.data.length) setStudentId(r.data[0].id);
      });
    } else {
      setStudentId(user?.id);
    }
  }, [user]);

  useEffect(() => {
    if (!studentId) return;
    if (user?.role === "parent") {
      api.get(`/parent/consumption/${studentId}?days=${days}`).then(r => setData(r.data));
    } else {
      api.get("/orders/me").then(r => {
        const orders = r.data;
        const since = Date.now() - days * 86400000;
        const filtered = orders.filter(o => new Date(o.created_at).getTime() >= since);
        const daily = {};
        const items = {};
        let total = 0;
        for (const o of filtered) {
          const d = o.created_at.slice(0, 10);
          daily[d] = (daily[d] || 0) + o.total;
          total += o.total;
          for (const it of o.items) items[it.name] = (items[it.name] || 0) + it.qty;
        }
        setData({
          total_spent: total,
          order_count: filtered.length,
          daily: Object.entries(daily).sort().map(([date, amount]) => ({ date, amount })),
          top_items: Object.entries(items).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([name, count]) => ({ name, count })),
          orders: filtered,
        });
      });
    }
  }, [studentId, days, user]);

  return (
    <AppShell variant={user?.role === "student" ? "student" : "parent"}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Consumption</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-ink mt-2">What's been on the plate</h1>
          </div>
          <div className="flex flex-wrap gap-2">
            {user?.role === "parent" && (
              <select
                data-testid="report-child"
                value={studentId || ""}
                onChange={(e) => setStudentId(e.target.value)}
                className="rounded-full border border-border bg-white px-4 py-2.5 text-sm font-semibold"
              >
                {children.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            )}
            <div className="inline-flex bg-cream-200 rounded-full p-1">
              {[7, 14, 30].map(d => (
                <button
                  key={d}
                  onClick={() => setDays(d)}
                  data-testid={`range-${d}`}
                  className={`px-4 py-1.5 rounded-full text-sm font-semibold ${days === d ? "bg-ink text-white" : "text-ink-700"}`}
                >
                  {d}d
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-5">
          <div className="bg-white rounded-3xl p-6 border border-border">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-ink-500">Total taps</div>
            <div className="font-heading text-4xl font-extrabold text-ink mt-2">{data?.order_count || 0}</div>
          </div>
          <div className="bg-white rounded-3xl p-6 border border-slate-200 bg-slate-50">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-700">Subscription</div>
            <div className="font-heading text-4xl font-extrabold text-slate-900 mt-2">{data?.by_source?.subscription || 0}</div>
          </div>
          <div className="bg-amber-50 rounded-3xl p-6 border border-amber-200">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-amber-700">Pre-order</div>
            <div className="font-heading text-4xl font-extrabold text-amber-900 mt-2">{data?.by_source?.preorder || 0}</div>
          </div>
          <div className="bg-rose-50 rounded-3xl p-6 border border-rose-200">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-rose-700">Special</div>
            <div className="font-heading text-4xl font-extrabold text-rose-900 mt-2">{data?.by_source?.special || 0}</div>
          </div>
        </div>

        <div className="bg-white rounded-3xl p-6 border border-border mb-5">
          <div className="flex items-center gap-2 text-ink-500 text-xs font-bold uppercase tracking-[0.2em]">
            <TrendingUp size={14} /> Daily taps
          </div>
          <div className="h-64 mt-3">
            {data?.daily?.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.daily}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EAE7DD" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#78716C" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#78716C" }} allowDecimals={false} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E7E5E4", fontSize: 12 }} />
                  <Line type="monotone" dataKey="count" stroke="#D12D2D" strokeWidth={3} dot={{ r: 4, fill: "#D12D2D" }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-sm text-ink-500">No taps in this range</div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-3xl p-6 border border-border">
          <div className="flex items-center gap-2 text-ink-500 text-xs font-bold uppercase tracking-[0.2em]">
            <Receipt size={14} /> Recent taps
          </div>
          <div className="mt-3 space-y-2 max-h-[480px] overflow-y-auto">
            {data?.taps?.slice(0, 50).map(o => {
              const accent = o.source === "subscription" ? "bg-slate-100 text-slate-700"
                : o.source === "preorder" ? "bg-amber-100 text-amber-700"
                : o.source === "special" ? "bg-rose-100 text-rose-700"
                : "bg-cream-200 text-ink-700";
              return (
                <div key={o.id} className="p-3 bg-cream-100 rounded-xl text-sm" data-testid={`tap-${o.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${accent}`}>{o.source}</span>
                      <span className="font-semibold text-ink">{o.date}</span>
                      {o.slot && <span className="text-xs text-ink-500 capitalize">· {o.slot}</span>}
                    </div>
                    {o.amount > 0 && <span className="font-bold">{inr(o.amount)}</span>}
                  </div>
                  {o.plan_name && <div className="text-xs text-ink-500 mt-1 truncate">{o.plan_name}</div>}
                </div>
              );
            })}
            {!data?.taps?.length && <div className="text-sm text-ink-500">No taps recorded yet</div>}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
